import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  TableRow,
  TableCell,
} from "@mui/material";
import UploadFileUI from "./uploadUi";
import { endpoints } from "../services/endpoints";
import authFetch from "../utils/authFetch";
import { REFRESH_INTERVAL } from "./constants";
import { processExcelEntries } from "./helpers";
import TableHeader from "./TableHeader";
import CustomTableRow from "./TableRow";
import ValidationModal from "./ValidationModal";
import DeleteDialog from "./DeleteDialog";

const UploadTab = () => {
  // State management
  const [excelEntries, setExcelEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [currentEntryErrors, setCurrentEntryErrors] = useState([]);

  // Fetch excel entries from API
  const fetchExcelEntries = async () => {
    try {
      const response = await authFetch.get(
        `${process.env.REACT_APP_BACKEND_URL}${endpoints.EXEL_ENTRIES}`,
        { headers: { Accept: "application/json" } }
      );

      if (response.data.success && Array.isArray(response.data.data)) {
        setExcelEntries(processExcelEntries(response.data.data));
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      setError(err.response ? `Error: ${err.response.status}` : err.message);
    }
  };

  // Initial load + refresh every 10 seconds
  useEffect(() => {
    setLoading(true);
    fetchExcelEntries().finally(() => setLoading(false));

    const intervalId = setInterval(fetchExcelEntries, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  // Event handlers
  const handleShowValidationErrors = (entry) => {
    setCurrentEntryErrors(entry.validationFailedReason || []);
    setShowValidationModal(true);
  };

  const handleValidate = async (excelId) => {
    setValidatingId(excelId);
    try {
      // Get browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      await authFetch.post(
        `${endpoints.VALIDATE_EXCEL}`,
        { excelId },
        {
          headers: {
            "x-user-timezone": browserTimezone,
          },
        }
      );
      fetchExcelEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setValidatingId(null);
    }
  };

  const handleProcess = async (excelId) => {
    setProcessingId(excelId);
    try {
      await authFetch.post(
        `${process.env.REACT_APP_BACKEND_URL}/upload/admin/process-excel`,
        { excelId }
      );
      fetchExcelEntries();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to process file");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setEntryToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    setDeletingId(entryToDelete._id);
    try {
      await authFetch.delete(
        `${process.env.REACT_APP_BACKEND_URL}${endpoints.DELETE_EXCEL}`,
        {
          data: { excelId: entryToDelete._id },
        }
      );
      fetchExcelEntries();
      setShowDeleteDialog(false);
      setEntryToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete file");
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadSuccess = () => {
    fetchExcelEntries();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Excel File Management
      </Typography>

      <UploadFileUI onUploadSuccess={handleUploadSuccess} />

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Uploaded Excel Files
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : (
          <TableContainer>
            <Table>
              <TableHeader />
              <TableBody>
                {excelEntries.length > 0 ? (
                  excelEntries.map((entry) => (
                    <CustomTableRow
                      key={entry._id}
                      entry={entry}
                      validatingId={validatingId}
                      processingId={processingId}
                      deletingId={deletingId}
                      onValidate={handleValidate}
                      onProcess={handleProcess}
                      onShowValidationErrors={handleShowValidationErrors}
                      onDelete={handleDeleteClick}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No files found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modals and Dialogs */}
      <ValidationModal
        open={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        currentEntryErrors={currentEntryErrors}
      />

      <DeleteDialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        entryToDelete={entryToDelete}
      />

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default UploadTab;
